import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from './cn';

/** Ant Design `InputNumber` replacement — controlled numeric input with min/max/step,
 *  optional formatter/parser (e.g. currency) and addon/prefix slots. onChange emits number|null. */
export function InputNumber({
  value, onChange, min, max, step = 1, precision, formatter, parser, prefix, addonAfter, addonBefore,
  disabled, readOnly, placeholder, size = 'middle', className, style, id, controls = true, stringMode, status, invalid,
}: {
  value?: number | null;
  onChange?: (v: number | null) => void;
  min?: number; max?: number; step?: number; precision?: number;
  formatter?: (v: number | string | undefined) => string;
  parser?: (s: string) => string | number;
  prefix?: ReactNode; addonAfter?: ReactNode; addonBefore?: ReactNode;
  disabled?: boolean; readOnly?: boolean; placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  className?: string; style?: CSSProperties; id?: string; controls?: boolean; stringMode?: boolean;
  status?: 'error' | 'warning'; invalid?: boolean;
}) {
  const isErr = invalid || status === 'error';
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focused) return;
    setText(value == null || Number.isNaN(value) ? '' : String(value));
  }, [value, focused]);

  const clamp = (n: number) => {
    let x = n;
    if (min != null) x = Math.max(min, x);
    if (max != null) x = Math.min(max, x);
    if (precision != null) x = Number(x.toFixed(precision));
    return x;
  };
  const parse = (raw: string): number | null => {
    const cleaned = parser ? parser(raw) : raw.replace(/[^\d.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
    const n = Number(cleaned);
    return Number.isNaN(n) ? null : n;
  };
  const emit = (raw: string) => {
    setText(raw);
    const n = parse(raw);
    onChange?.(n == null ? null : clamp(n));
  };
  const bump = (dir: 1 | -1) => {
    const cur = value ?? 0;
    onChange?.(clamp(cur + dir * step));
  };

  const h = size === 'small' ? 'h-8 text-[13px]' : size === 'large' ? 'h-11 text-[15px]' : 'h-9 text-sm';
  const display = !focused && formatter ? formatter(value ?? undefined) : text;

  const field = (
    <div className={cn('relative flex items-stretch overflow-hidden rounded-control border bg-surface transition-colors focus-within:ring-2 focus-within:ring-brand-500/20', isErr ? 'border-danger-500' : 'border-line focus-within:border-brand-400', disabled && 'cursor-not-allowed opacity-60', addonBefore || addonAfter ? '' : 'w-full', className)}>
      {prefix != null && <span className="flex items-center pl-3 text-muted">{prefix}</span>}
      <input
        ref={ref}
        id={id}
        type="text"
        inputMode="decimal"
        role="spinbutton"
        aria-valuenow={value ?? undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        value={display}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); if (text !== '') { const n = parse(text); onChange?.(n == null ? null : clamp(n)); } }}
        onChange={(e) => emit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); bump(1); } else if (e.key === 'ArrowDown') { e.preventDefault(); bump(-1); } }}
        className={cn('w-full min-w-0 bg-transparent px-3 text-content outline-none placeholder:text-subtle', h, prefix != null && 'pl-1.5', controls && 'pr-7')}
        style={style}
      />
      {controls && !disabled && !readOnly && (
        <div className="absolute right-0 top-0 flex h-full w-6 flex-col border-l border-line">
          <button type="button" tabIndex={-1} aria-label="Increase" onClick={() => bump(1)} className="flex flex-1 items-center justify-center text-subtle hover:bg-surface-2 hover:text-content">
            <svg viewBox="0 0 12 12" className="size-2.5" fill="currentColor"><path d="M6 3l4 5H2z" /></svg>
          </button>
          <button type="button" tabIndex={-1} aria-label="Decrease" onClick={() => bump(-1)} className="flex flex-1 items-center justify-center border-t border-line text-subtle hover:bg-surface-2 hover:text-content">
            <svg viewBox="0 0 12 12" className="size-2.5" fill="currentColor"><path d="M6 9L2 4h8z" /></svg>
          </button>
        </div>
      )}
    </div>
  );

  if (!addonBefore && !addonAfter) return field;
  return (
    <div className="flex items-stretch">
      {addonBefore != null && <span className="flex items-center rounded-l-control border border-r-0 border-line bg-surface-2 px-3 text-sm text-muted">{addonBefore}</span>}
      <div className="flex-1 [&>div]:rounded-none [&>div]:first:rounded-l-control [&>div]:last:rounded-r-control">{field}</div>
      {addonAfter != null && <span className="flex items-center rounded-r-control border border-l-0 border-line bg-surface-2 px-3 text-sm text-muted">{addonAfter}</span>}
    </div>
  );
}
